import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { PropertyDto, PropertyRoomType } from '@workspace/data-types';
import { EtxeEnumsNiceLabelUtils } from '../../../utils/etxe-enums-nice-label.utils';
import { EtxeEnumDisplayPipe } from '../../pipes/etxe-enum-display.pipe';

@Component({
  selector: 'etxe-property-hover-card',
  standalone: true,
  imports: [DecimalPipe, MatIconModule, EtxeEnumDisplayPipe],
  templateUrl: './property-hover-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyHoverCardComponent {
  property = input.required<PropertyDto>();

  // Cover photo URL
  coverPhotoUrl = computed<string | null>(() => {
    const cp = this.property().media?.coverPhoto;
    if (cp && typeof cp === 'object' && 'url' in cp && typeof cp.url === 'string') {
      return cp.url;
    }
    return null;
  });

  // Reference (e.g. business reference VM-101 or cadastral or ID last 6 digits if draft)
  reference = computed(() => {
    const prop = this.property();
    if (prop.definition?.reference) {
      return prop.definition.reference;
    }
    const firstParcelRef = prop.geospatial?.cadastral?.parcels?.[0]?.reference;
    if (firstParcelRef) {
      return firstParcelRef;
    }
    if (prop._id) {
      return String(prop._id).slice(-6).toUpperCase();
    }
    return '-';
  });

  // Living room surface
  livingRoomSurface = computed<number | null>(() => {
    const rooms = this.property().characteristics?.interior?.rooms;
    if (!rooms || !Array.isArray(rooms)) return null;
    const living = rooms.find((r) => r.type === PropertyRoomType.LIVING_ROOM);
    return living?.surface && living.surface > 0 ? living.surface : null;
  });

  // Total rooms count
  totalRooms = computed<number | null>(() => {
    const interior = this.property().characteristics?.interior;
    if (!interior) return null;

    let count = 0;
    if (Array.isArray(interior.bedroom)) {
      count += interior.bedroom.length;
    }
    if (Array.isArray(interior.rooms)) {
      // Eligible main rooms
      const mainTypes = new Set<PropertyRoomType>([
        PropertyRoomType.LIVING_ROOM,
        PropertyRoomType.DINING_ROOM,
        PropertyRoomType.OFFICE,
        PropertyRoomType.LIBRARY,
        PropertyRoomType.PLAYROOM,
        PropertyRoomType.MUSIC_STUDIO,
      ]);
      count += interior.rooms.filter((r) => !r.type || mainTypes.has(r.type)).length;
    }
    return count > 0 ? count : null;
  });

  // Bedroom count
  bedroomCount = computed<number | null>(() => {
    const count = this.property().characteristics?.interior?.bedroom?.length;
    return count && count > 0 ? count : null;
  });

  // Heating summary
  heatingLabel = computed<string>(() => {
    const tech = this.property().dpe?.technical;
    const typeStr = tech?.heatingType || '';
    const energyStr = tech?.heatingEnergy || '';

    if (typeStr && energyStr) {
      return `${typeStr} (${energyStr})`;
    }
    return typeStr || energyStr || 'Non renseigné';
  });

  // Views summary
  viewsLabel = computed<string | null>(() => {
    const views = this.property().construction?.view;
    if (views && views.length > 0) {
      return views.map((v) => EtxeEnumsNiceLabelUtils.propertyView(v)).join(', ');
    }
    return null;
  });

  // Mandate type label
  mandateTypeLabel = computed<string>(() => {
    const type = this.property().mandate?.type;
    return EtxeEnumsNiceLabelUtils.mandateType(type);
  });

  // Mandate commission rate
  targetCommissionRate = computed<number | null>(() => {
    return this.property().mandate?.targetCommissionRate ?? null;
  });

  // Mandate duration label
  mandateDurationLabel = computed<string | null>(() => {
    const dur = this.property().mandate?.duration;
    return dur ? EtxeEnumsNiceLabelUtils.mandateDuration(dur) : null;
  });

  // Mandate priorities
  mandatePriorities = computed<string[]>(() => {
    const priorities = this.property().mandate?.priorities;
    if (!priorities || !Array.isArray(priorities)) return [];
    return priorities.map((p) => EtxeEnumsNiceLabelUtils.sellerPriority(p)).filter(Boolean);
  });

  // Availability label
  availabilityLabel = computed<string>(() => {
    const avail = this.property().keyInfo?.availability;
    if (avail?.isOccupied) {
      return 'Occupé';
    }
    if (avail?.isOccupied === false) {
      return 'Libre';
    }
    return 'Disponible';
  });

  // Price per square meter
  pricePerSquareMeter = computed<number | null>(() => {
    const price = this.property().financials?.sellingPriceCents;
    const surface = this.property().keyInfo?.surface?.habitable;
    if (price && surface && surface > 0) {
      return Math.round(price / 100 / surface);
    }
    return null;
  });

  // DPE Color badge background helper
  getDpeColor(grade?: string | null): string {
    switch (grade?.toUpperCase()) {
      case 'A':
        return '#00803C';
      case 'B':
        return '#54B345';
      case 'C':
        return '#CED932';
      case 'D':
        return '#FFF200';
      case 'E':
        return '#FCBA08';
      case 'F':
        return '#EB8235';
      case 'G':
        return '#D71D20';
      default:
        return '#9CA3AF';
    }
  }

  isDarkDpeText(grade?: string | null): boolean {
    return ['C', 'D', 'E'].includes(grade?.toUpperCase() ?? '');
  }
}
